import { ref } from 'vue'

/**
 * Composable for handling async code within Vue.
 * @template TInitial
 * @template TResult
 * @template {unknown[]} [TArgs=[]]
 * @param {UseAsyncSource<TResult, TArgs>} source
 * @param {object} [options]
 * @param {boolean} [options.immediate]
 * @param {TInitial} [options.initialValue]
 * @param {boolean} [options.resetOnTrigger]
 * @return {UseAsyncState<TInitial, TResult, TArgs>}
 */
export default function useAsync(source, { immediate = true, initialValue = null, resetOnTrigger = false } = {}) {
  const pending = ref(immediate)
  const error = ref(null)
  const result = ref(initialValue)

  // This prevents overlapping trigger calls setting the result/error incorrectly
  // Only the latest result of a call to trigger will be applied to the store.
  let runId = 0

  const trigger = async (...args) => {
    const currentRun = ++runId
    pending.value = true
    error.value = null

    if (resetOnTrigger) {
      result.value = initialValue
    }

    try {
      // Normalize to a promise
      const res = await Promise.resolve(source instanceof Function ? source(...args) : source)
      if (currentRun === runId) {
        result.value = res
        pending.value = false
      }
    } catch (err) {
      if (currentRun === runId) {
        error.value = err
        pending.value = false
      }
    }
  }

  const reset = () => {
    pending.value = false
    error.value = null
    result.value = initialValue
    runId++
  }

  const cancel = () => {
    pending.value = false
    runId++
  }

  if (immediate) {
    trigger()
  }

  return { pending, error, result, trigger, reset, cancel }
}

/**
 * @template TResult
 * @template {unknown[]} [TArgs=[]]
 * @typedef {TResult | Promise<TResult> | ((...args: TArgs) => TResult) | ((...args: TArgs) => Promise<TResult>)} UseAsyncSource<TResult, TArgs>
 */

/**
 * @template TInitial,
 * @template TResult,
 * @template {unknown[]} [TArgs=[]]
 * @typedef {object} UseAsyncState
 * @prop {import('vue').Ref<boolean>} pending
 * @prop {import('vue').Ref<null>} error
 * @prop {import('vue').Ref<TInitial | TResult>} result
 * @prop {(...args: TArgs) => Promise<void>} trigger
 * @prop {() => void} reset
 * @prop {() => void} cancel
 */
